import { useTranslation } from 'react-i18next';
import { Star, Eye, Edit, Trash2 } from 'lucide-react';
import type { Supplier } from '../../../../api/queries/purchasing.queries';
import { PerformanceGauge, ActionMenu } from '../shared';

interface SupplierCardProps {
  supplier: Supplier;
  onView: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  canManage?: boolean;
}

export default function SupplierCard({
  supplier,
  onView,
  onEdit,
  onDelete,
  canManage = false,
}: SupplierCardProps) {
  const { t } = useTranslation();

  const initials = supplier.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  const onTimeRate = supplier.performanceMetrics?.onTimeDeliveryRate
    ? supplier.performanceMetrics.onTimeDeliveryRate * 100
    : 0;
  const qualityRating = supplier.performanceMetrics?.qualityRating || 0;

  const actions = [
    { label: t('common.view'), icon: Eye, onClick: onView },
    ...(canManage && onEdit ? [{ label: t('common.edit'), icon: Edit, onClick: onEdit }] : []),
    ...(canManage && onDelete
      ? [{ label: t('common.delete'), icon: Trash2, onClick: onDelete, variant: 'destructive' as const }]
      : []),
  ];

  return (
    <div
      className="rounded-xl border bg-card hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer group"
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onView()}
    >
      <div className="p-5">
        {/* Header with Avatar */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-bold text-primary">{initials}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground truncate">{supplier.name}</h3>
              {supplier.isPreferred && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs font-medium">
                  <Star className="h-3 w-3 fill-current" />
                  {t('purchasing.preferred')}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {supplier.contactPerson}
              {supplier.code && (
                <span className="text-muted-foreground/70"> • {supplier.code}</span>
              )}
            </p>
          </div>

          <div onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} />
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mt-4 grid grid-cols-3 gap-3 p-4 rounded-lg bg-secondary/30">
          <PerformanceGauge
            value={onTimeRate}
            max={100}
            type="percentage"
            label={t('purchasing.onTimeRate')}
            size="sm"
          />
          <PerformanceGauge
            value={qualityRating}
            max={5}
            type="stars"
            label={t('purchasing.qualityRating')}
            size="sm"
          />
          <PerformanceGauge
            value={supplier.leadTimeDays || 0}
            max={1}
            type="number"
            label={t('purchasing.leadTime')}
            size="sm"
          />
        </div>

        {/* Contact Info */}
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span>{supplier.phone}</span>
          <span className="truncate">{supplier.email}</span>
        </div>
      </div>
    </div>
  );
}
